import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { PageTitle } from '../PageTitle';
import { ReactComponent as AddIcon } from '../../assets/svg/add-icon.svg';
import IconSearch from '../../assets/svg/search-icon-grey.svg';

export interface ListPageHeaderProps {
  title: string;
  subtitle?: string;
  actionLabel?: string;
  onActionClick?: () => void;
  actionIcon?: ReactNode;
  // Disables the primary action; actionTitle explains why on hover.
  actionDisabled?: boolean;
  actionTitle?: string;
  // Outline button rendered to the left of the primary action.
  secondaryActionLabel?: string;
  onSecondaryActionClick?: () => void;
  titleExtra?: ReactNode;
  // Segmented view toggle (e.g. Catalog | Discovered). Rendered on its own
  // row under the title, left-aligned, so it sits in the same spot on both
  // halves of a page pair regardless of title length or header actions.
  tabs?: ReactNode;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  searchInputId?: string;
  searchRightContent?: ReactNode;
  /** >0: the input owns its text locally and writes searchValue after this
   *  pause — keystrokes re-render only the header, not every URL subscriber.
   *  0 (default): controlled write-through, exactly as before. */
  searchDebounceMs?: number;
}

export const ListPageHeader: React.FC<ListPageHeaderProps> = ({
  title,
  subtitle,
  actionLabel,
  onActionClick,
  actionIcon,
  actionDisabled,
  actionTitle,
  secondaryActionLabel,
  onSecondaryActionClick,
  titleExtra,
  tabs,
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search',
  searchInputId,
  searchRightContent,
  searchDebounceMs = 0,
}) => {
  const showSearch = searchValue !== undefined && onSearchChange !== undefined;

  const debounced = searchDebounceMs > 0;
  const [draft, setDraft] = useState(searchValue ?? '');
  // The last value we pushed: tells our own echo (searchValue catching up to
  // draft) apart from an external write (chip clear, back button), which must
  // overwrite the draft.
  const pendingRef = useRef<string | null>(null);
  useEffect(() => {
    if (!debounced) return;
    if (searchValue === pendingRef.current) {
      pendingRef.current = null;
      return;
    }
    setDraft(searchValue ?? '');
  }, [searchValue, debounced]);
  useEffect(() => {
    if (!debounced) return undefined;
    if (draft === (searchValue ?? '')) return undefined;
    const t = setTimeout(() => {
      pendingRef.current = draft;
      onSearchChange?.(draft);
    }, searchDebounceMs);
    return () => clearTimeout(t);
  }, [draft, debounced, searchDebounceMs, searchValue, onSearchChange]);

  return (
    <>
      <div className={`w-full flex justify-between items-center gap-3 ${tabs ? 'mb-3' : 'mb-6'}`}>
        <div className="flex flex-col min-w-0">
          <div className="flex justify-between items-center gap-2">
            <PageTitle title={title} />
            {titleExtra}
          </div>
          {subtitle && (
            <p className="text-[12px] sm:text-[13px] text-text-2 mt-1 truncate">{subtitle}</p>
          )}
        </div>
        {(actionLabel || secondaryActionLabel) && (
          <div className="flex items-center gap-2 shrink-0">
            {secondaryActionLabel && (
              <button
                type="button"
                onClick={onSecondaryActionClick}
                className="text-[14px] font-semibold text-text-2 border border-border-token rounded-xl px-4 py-2.5 bg-transparent cursor-pointer hover:bg-surface-2 whitespace-nowrap"
              >
                {secondaryActionLabel}
              </button>
            )}
            {actionLabel && (
              <button
                type="button"
                onClick={onActionClick}
                disabled={actionDisabled}
                title={actionTitle}
                className="flex items-center gap-1 bg-primary text-sm sm:text-base text-white py-2.5 sm:py-3 px-4 sm:px-5 lg:px-10 cursor-pointer rounded-xl focus:outline-none focus:shadow-outline whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {actionIcon ?? <AddIcon className="mr-1" />}
                {actionLabel}
              </button>
            )}
          </div>
        )}
      </div>

      {tabs && <div className="flex items-center mb-4">{tabs}</div>}

      {showSearch && (
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 pt-2 pb-6 sm:pb-8">
          <div className="w-full relative sm:max-w-[333px]">
            <img
              className="absolute top-1/2 transform -translate-x-1/2 -translate-y-1/2 left-5"
              alt="search-icon"
              src={IconSearch}
            />
            <input
              type="text"
              className="w-full h-[40px] px-[2.25rem] py-0 gap-2 bg-surface-2 text-text-2 placeholder-muted rounded-xl focus:outline-none focus:text-text-1"
              id={searchInputId}
              placeholder={searchPlaceholder}
              value={debounced ? draft : searchValue}
              onChange={(e) => (debounced ? setDraft(e.target.value) : onSearchChange(e.target.value))}
            />
          </div>
          {searchRightContent && (
            <div className="flex items-center gap-2 justify-end">
              {searchRightContent}
            </div>
          )}
        </div>
      )}
    </>
  );
};
